import { useOrgUsers } from '@/utils/hooks/invitations'
import {
  Typography,
  List,
  ListItem,
  Box,
  Divider,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material'
import EditIcon from '@mui/icons-material/Edit'
import { useState } from 'react'
import { OrgUser } from '@/models/Users'

interface OrgUsersListProps {
  orgUsers: OrgUser[]
  orgUuid: string
  token: string
  isMobile: boolean
}

export default function OrgUsersList({
  orgUsers,
  orgUuid,
  token,
  isMobile,
}: OrgUsersListProps) {
  const { deleteOrgUser, updateOrgUser } = useOrgUsers(token, orgUuid)
  const [deleteUserError, setDeleteUserError] = useState<
    { userUuid: string; message: string }[]
  >([])
  const [deleting, setDeleting] = useState<boolean>(false)
  const [userToUpdate, setUserToUpdate] = useState<string | null>(null)
  const [targetRole, setTargetRole] = useState<'admin' | 'user'>('admin')
  const [updateUserError, setUpdateUserError] = useState<string | null>(null)
  const [showUpdateConfirmation, setShowUpdateConfirmation] =
    useState<boolean>(false)

  async function handleDeleteOrgUser(userUuid: string) {
    if (!token) {
      return
    }
    setDeleting(true)
    setDeleteUserError((prev) => prev.filter((e) => e.userUuid !== userUuid))
    try {
      await deleteOrgUser.mutateAsync({
        userUuid,
        orgUuid,
        token,
      })
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Failed to remove user from organisation'
      setDeleteUserError((prev) => [...prev, { userUuid, message }])
    }
    setDeleting(false)
  }

  async function handleUpdateOrgUserRole(
    confirmed: boolean,
    userUuid: string | null,
    role: 'admin' | 'user' = 'admin',
  ) {
    if (!token || !orgUuid || !userUuid) {
      return
    }
    setUserToUpdate(userUuid)
    setTargetRole(role)
    if (!confirmed) {
      setUpdateUserError(null)
      setShowUpdateConfirmation(true)
      return
    }

    try {
      await updateOrgUser.mutateAsync({
        userUuid,
        orgUuid,
        role,
        token,
      })
      setShowUpdateConfirmation(false)
      setUserToUpdate(null)
    } catch (err) {
      setUpdateUserError(
        err instanceof Error ? err.message : 'Failed to update user role',
      )
    }
  }

  return (
    <>
      <Dialog
        open={showUpdateConfirmation}
        onClose={() => {
          setShowUpdateConfirmation(false)
          setUpdateUserError(null)
        }}
        disableRestoreFocus
        aria-labelledby="warning-dialog-title"
        aria-describedby="warning-dialog-description"
      >
        <DialogTitle id="warning-dialog-title">
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography variant="h6" sx={{ mt: 1 }}>
              Heads up! <br />
              <span style={{ fontWeight: '400', fontSize: '18px' }}>
                You're about to change the role of someone in your organisation.
              </span>
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="warning-dialog-description"
            sx={{ color: '#1C3045', mt: 2 }}
          >
            <Typography variant="body1" sx={{ mb: 2 }}>
              You are about to change their role to{' '}
              <strong>{targetRole}</strong>.
            </Typography>
            <Box
              sx={{
                p: 2,
                border: '1px solid',
                borderColor: 'warning.light',
                borderRadius: 1,
                backgroundColor: '#FFF8E1',
              }}
            >
              <Typography
                variant="body2"
                sx={{ fontWeight: 600, mb: 0.5, color: '#1C3045' }}
              >
                {targetRole === 'admin'
                  ? 'This means they will be able to:'
                  : 'This means they will no longer be able to:'}
              </Typography>
              <Typography
                component="ul"
                variant="body2"
                sx={{ m: 0, pl: 3, color: '#1C3045' }}
              >
                <li>Invite new users to your organisation</li>
                <li>Remove users from your organisation</li>
                <li>Change other users' roles</li>
                <li>Access the Account Billing area</li>
              </Typography>
            </Box>
          </DialogContentText>
          {updateUserError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {updateUserError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setShowUpdateConfirmation(false)
              setUserToUpdate(null)
            }}
            variant="outlined"
            disableRipple
            sx={{
              color: '#1C3045',
              borderColor: '#1C3045',
              textTransform: 'none',
              fontSize: '16px',
              width: '100px',
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              handleUpdateOrgUserRole(true, userToUpdate || null, targetRole)
            }}
            variant="contained"
            disableRipple
            sx={{
              color: 'white',
              bgcolor: '#1C3045',
              textTransform: 'none',
              fontSize: '16px',
              width: '100px',
            }}
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>
      {isMobile ? (
        <List>
          {orgUsers.map((orgUser: OrgUser) => (
            <Box key={orgUser.uuid}>
              <ListItem>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {orgUser.first_name || orgUser.last_name ? (
                    `${orgUser.first_name} ${orgUser.last_name}`
                  ) : (
                    <span style={{ fontWeight: 400, color: 'grey' }}>
                      Not provided yet
                    </span>
                  )}{' '}
                  - {orgUser.role}
                </Typography>
              </ListItem>
              <ListItem>
                <Typography variant="body1">{orgUser.email}</Typography>
              </ListItem>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 1,
                  mb: 1,
                }}
              >
                {(() => {
                  const err = deleteUserError.find(
                    (e) => e.userUuid === orgUser.uuid,
                  )
                  return err ? (
                    <Alert severity="error">{err.message}</Alert>
                  ) : null
                })()}
                <Button
                  variant="text"
                  disableRipple
                  startIcon={<EditIcon />}
                  sx={{
                    color: '#1C3045',
                    fontWeight: 500,
                    textTransform: 'none',
                    '&:focus': {
                      backgroundColor: '#e8e8e8',
                    },
                  }}
                  onClick={() =>
                    handleUpdateOrgUserRole(
                      false,
                      orgUser.uuid,
                      orgUser.role === 'admin' ? 'user' : 'admin',
                    )
                  }
                >
                  Change Role
                </Button>
                <Button
                  variant="outlined"
                  disabled={deleting}
                  disableRipple
                  sx={{
                    borderColor: '#1C3045',
                    color: '#1C3045',
                    borderRadius: 2,
                    borderWidth: 2,
                    fontWeight: 500,
                    textTransform: 'none',
                    '&:focus': {
                      backgroundColor: '#e8e8e8',
                    },
                  }}
                  onClick={() => handleDeleteOrgUser(orgUser.uuid)}
                >
                  {deleting ? 'Removing...' : 'Remove User'}
                </Button>
              </Box>

              <Divider />
            </Box>
          ))}
        </List>
      ) : (
        <List>
          <ListItem
            sx={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr 1fr 1fr',
              width: '100%',
              height: '50px',
              mb: 1,
            }}
          >
            <Typography
              variant="body1"
              sx={{
                textAlign: 'left',
                fontSize: '18px',
              }}
            >
              Name
            </Typography>
            <Typography
              variant="body1"
              sx={{
                textAlign: 'left',
                fontSize: '18px',
              }}
            >
              Email
            </Typography>
            <Typography
              variant="body1"
              sx={{
                textAlign: 'center',
                fontSize: '18px',
              }}
            >
              Role
            </Typography>
          </ListItem>
          <Divider />

          {orgUsers.map((orgUser: OrgUser) => (
            <Box key={orgUser.uuid}>
              <ListItem
                sx={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr 1fr 1fr',
                  width: '100%',
                  height: '50px',
                  mb: 1,
                }}
              >
                <Typography
                  variant="body1"
                  sx={{
                    textAlign: 'left',
                    fontSize: '16px',
                  }}
                >
                  {orgUser.first_name || orgUser.last_name ? (
                    `${orgUser.first_name} ${orgUser.last_name}`
                  ) : (
                    <span style={{ fontWeight: 400, color: 'grey' }}>
                      Not provided yet
                    </span>
                  )}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    textAlign: 'left',
                    fontSize: '16px',
                    overflow: 'scroll',
                    maxWidth: '100%',
                  }}
                >
                  {orgUser.email}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    position: 'relative',
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      fontSize: '16px',
                      textAlign: 'center',
                    }}
                  >
                    {orgUser.role}
                  </Typography>
                </Box>
                <Box
                  sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}
                >
                  {(() => {
                    const err = deleteUserError.find(
                      (e) => e.userUuid === orgUser.uuid,
                    )
                    return err ? (
                      <Alert severity="error">{err.message}</Alert>
                    ) : null
                  })()}
                  <Button
                    variant="text"
                    disableRipple
                    startIcon={<EditIcon />}
                    sx={{
                      borderColor: '#1C3045',
                      color: '#1C3045',
                      borderRadius: 2,
                      borderWidth: 2,
                      fontWeight: 500,
                      textTransform: 'none',
                      '&:focus': {
                        backgroundColor: '#e8e8e8',
                      },
                    }}
                    onClick={() =>
                      handleUpdateOrgUserRole(
                        false,
                        orgUser.uuid,
                        orgUser.role === 'admin' ? 'user' : 'admin',
                      )
                    }
                  >
                    Change Role
                  </Button>
                  <Button
                    variant="outlined"
                    disabled={deleting}
                    disableRipple
                    sx={{
                      borderColor: '#1C3045',
                      color: '#1C3045',
                      borderRadius: 2,
                      borderWidth: 2,
                      fontWeight: 500,
                      textTransform: 'none',
                      '&:focus': {
                        backgroundColor: '#e8e8e8',
                      },
                    }}
                    onClick={() => handleDeleteOrgUser(orgUser.uuid)}
                  >
                    {deleting ? 'Removing...' : 'Remove User'}
                  </Button>
                </Box>
              </ListItem>
              <Divider />
            </Box>
          ))}
        </List>
      )}
    </>
  )
}
